import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { NbButtonModule, NbDialogService, NbIconModule } from '@nebular/theme';
import { Conversion, ConversionShared } from 'src/app/models/conversion.model';
import { FeatureFlagService } from 'src/app/shared/services/feature-flag.service';
import { XeroBankFeedService, XeroPushState } from 'src/app/shared/services/xero-bank-feed.service';
import { SendToXeroDialogComponent } from 'src/app/home/xero/send-to-xero-dialog/send-to-xero-dialog.component';

/**
 * Self-contained "Send to Xero" affordance for a conversion row.
 * Owns the feature-flag gate, eligibility display, dialog launch, and
 * post-push status — hosts just render <app-xero-send-control [conversion]>.
 * Renders nothing when the feature is off or the conversion isn't pushable.
 *
 * Rendered twice per row via `slot`: "action" is the send/retry button in the
 * action cluster; "status" is the quiet delivered/pending state in the meta line.
 */
@Component({
  selector: 'app-xero-send-control',
  standalone: true,
  imports: [CommonModule, NbButtonModule, NbIconModule],
  templateUrl: './xero-send-control.component.html',
  styleUrls: ['./xero-send-control.component.scss'],
})
export class XeroSendControlComponent implements OnInit {
  /** Full Conversion in the user app; the shared shape keeps the row component reusable */
  @Input() conversion: ConversionShared;
  @Input() slot: 'action' | 'status' = 'action';

  enabled = false;
  connected = false;

  constructor(
    private featureFlag: FeatureFlagService,
    private dialogService: NbDialogService,
    private bankFeed: XeroBankFeedService,
  ) {}

  async ngOnInit() {
    this.enabled = await this.featureFlag.isEnabled('xeroBankFeedsEnabled');
    if (!this.enabled) return;

    // No usable connection means the button could only ever open a dialog that
    // asks the user to connect — so don't offer it. Reconnecting lives in
    // Profile → Xero Settings, where the connection state is explained.
    this.connected = await this.bankFeed.isConnected();
    if (!this.connected) return;

    // Warm the accounts cache (once per session) so the dialog opens instantly
    this.bankFeed.prefetchAccounts();

    // Xero settles deliveries asynchronously and never calls back, so a push the
    // send dialog left PENDING would read "processing…" forever. Reconcile it
    // once here — the 'status' slot only, so the row asks exactly once.
    if (this.slot === 'status' && this.status === 'PENDING') this.settlePendingPush();
  }

  /** Best-effort background sync; silent because the user didn't ask for it */
  private async settlePendingPush() {
    const response = await this.bankFeed.refreshStatus(this.conversion.uid, true);
    if (response?.xeroPush) this.conversion.xeroPush = response.xeroPush;
  }

  get status(): XeroPushState['status'] | null {
    return this.conversion?.xeroPush?.status || null;
  }

  /** Connected user, with a successful (or corrected) conversion not yet sent */
  get canSend(): boolean {
    if (!this.enabled || !this.connected || !this.conversion) return false;
    if (!this.conversion.csvDownloadLink || this.conversion.deleted) return false;
    if (this.status === 'DELIVERED' || this.status === 'PENDING') return false;
    return !!this.conversion.healthCheck?.healthy || !!this.conversion.corrected;
  }

  openDialog() {
    this.dialogService
      .open(SendToXeroDialogComponent, {
        context: { conversion: this.conversion as Conversion },
        hasBackdrop: true,
        closeOnBackdropClick: false,
      })
      .onClose.subscribe((push: XeroPushState | null) => {
        if (push) this.conversion.xeroPush = push;
      });
  }
}
